/**
 * OPA Client for frontend permission checks
 */

import { User } from '@maxiron/shared';

const OPA_URL = import.meta.env.VITE_OPA_URL || 'http://localhost:8181';
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

/**
 * In-memory cache for OPA decisions
 */
const decisionCache = new Map<string, { result: boolean; expiresAt: number }>();
const CACHE_TTL = 10000; // 10 seconds

/**
 * Simple permission check using user's permissions object (fallback for mock mode)
 */
function checkPermissionLocal(user: User, page: string, action: string): boolean {
  // Admin bypass - grant all access
  if (user.flags?.is_admin) {
    return true;
  }
  
  // Full access flag
  if (user.flags?.all_pages_all_options) {
    return true;
  }

  // Read-only access to all pages
  if (user.flags?.all_pages) {
    return action === 'read';
  }

  // Manage default params: allow via dedicated flag or workflows action
  if (page === 'workflows' && action === 'manage_default_params') {
    if (user.flags?.can_manage_default_params) return true;
  }

  // Check page-specific permissions
  if (user.permissions && page in user.permissions) {
    const pagePerms = user.permissions[page as keyof typeof user.permissions];
    if (typeof pagePerms === 'object' && action in pagePerms) {
      return pagePerms[action as keyof typeof pagePerms] === true;
    }
  }
  
  return false;
}

/**
 * Query OPA for a permission decision
 */
export async function checkPermission(
  user: User,
  page: string,
  action: string
): Promise<boolean> {
  // Always use local permission check in frontend (OPA enforcement happens on backend)
  // Direct frontend→OPA calls require CORS configuration which OPA doesn't support easily
  return checkPermissionLocal(user, page, action);
  
  const cacheKey = `${user.id}:${page}:${action}`;
  
  // Check cache first
  const cached = decisionCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.result;
  }
  
  try {
    const input = {
      user: {
        id: user.id,
        email: user.email,
        permissions: user.permissions,
        tenant_access: user.tenant_access,
        flags: user.flags,
        page_flags: user.page_flags,
        groups: user.groups,
      },
      page,
      action,
    };
    
    const response = await fetch(`${OPA_URL}/v1/data/maxiron/portal/rbac/allow`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ input }),
    });
    
    if (!response.ok) {
      console.error('OPA query failed:', response.status);
      return false;
    }
    
    const data = await response.json();
    const allow = data.result === true;
    
    // Cache decision
    decisionCache.set(cacheKey, {
      result: allow,
      expiresAt: Date.now() + CACHE_TTL,
    });
    
    return allow;
  } catch (error) {
    console.error('OPA query error, falling back to local permission check:', error);
    // Fallback to local permission check if OPA is unavailable
    return checkPermissionLocal(user, page, action);
  }
}

/**
 * Check multiple permissions in batch
 */
export async function checkPermissionsBatch(
  user: User,
  checks: Array<{ page: string; action: string }>
): Promise<Record<string, boolean>> {
  const results: Record<string, boolean> = {};
  
  // Query in parallel
  await Promise.all(
    checks.map(async (check) => {
      const key = `${check.page}:${check.action}`;
      results[key] = await checkPermission(user, check.page, check.action);
    })
  );
  
  return results;
}

/**
 * Check if user has access to a specific tenant
 */
export function hasTenantAccess(user: User, tenantCode: string): boolean {
  // Check flags first
  if (user.flags?.all_tenants) {
    return true;
  }
  
  // Check tenant_access array
  const wanted = String(tenantCode).toLowerCase();
  return (user.tenant_access || []).some((t) => String(t).toLowerCase() === wanted);
}

/**
 * Get list of accessible tenants
 */
export function getAccessibleTenants(user: User, allTenants: string[]): string[] {
  // Check flags first
  if (user.flags?.all_tenants) {
    return allTenants;
  }
  
  // Filter by tenant_access
  if (user.tenant_access && user.tenant_access.length > 0) {
    const allowed = new Set(user.tenant_access.map((t) => String(t).toLowerCase()));
    return allTenants.filter((t) => allowed.has(String(t).toLowerCase()));
  }
  
  return [];
}

/**
 * Clear permission cache (call after user logs in/out or permissions change)
 */
export function clearPermissionCache() {
  decisionCache.clear();
}

